<script setup>
import OrderProgress from '@/pages/order/components/OrderProgress.vue'
import { useRequireSelfOrderIdentity } from '@/composables/useRequireSelfOrderIdentity'
import { useSelfOrderStore } from '@/stores/selfOrder'
import { formatRupiah } from '@/utils/formatRupiah'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const router = useRouter()
const store = useSelfOrderStore()

const isIdentified = useRequireSelfOrderIdentity()

const isLoading = ref(true)
const loadError = ref('')
const activeCategory = ref('all')

const groupedMenu = computed(() => {
  const groups = {}

  for (const product of store.menu) {
    const categoryName = product.category || 'Lainnya'
    if (!groups[categoryName])
      groups[categoryName] = []
    groups[categoryName].push(product)
  }

  return groups
})

const categories = computed(() => Object.keys(groupedMenu.value))

const visibleGroups = computed(() => {
  if (activeCategory.value === 'all')
    return groupedMenu.value

  return { [activeCategory.value]: groupedMenu.value[activeCategory.value] || [] }
})

const loadMenu = async () => {
  isLoading.value = true
  loadError.value = ''
  try {
    await store.fetchMenu()
  }
  catch (err) {
    loadError.value = err?.data?.message || err?.message || 'Gagal memuat menu. Coba lagi.'
  }
  finally {
    isLoading.value = false
  }
}

onMounted(() => {
  if (isIdentified)
    loadMenu()
})

// ── Product detail dialog ──────────────────────────────────────────────────

const isDialogOpen = ref(false)
const activeProduct = ref(null)
const selectedVariantId = ref(null)
const selectedModifierIds = ref([])
const quantity = ref(1)
const notes = ref('')

const openProduct = product => {
  activeProduct.value = product
  selectedVariantId.value = product.variants?.[0]?.id ?? null
  selectedModifierIds.value = []
  quantity.value = 1
  notes.value = ''
  isDialogOpen.value = true
}

const selectedVariant = computed(() =>
  activeProduct.value?.variants?.find(v => v.id === selectedVariantId.value),
)

// Defensif: modifier_groups belum tentu ada di response (lihat catatan di stores/selfOrder.js)
const modifierGroups = computed(() => activeProduct.value?.modifier_groups || [])

const dialogTotal = computed(() => {
  if (!selectedVariant.value)
    return 0

  const modifierTotal = modifierGroups.value
    .flatMap(g => g.modifiers || [])
    .filter(m => selectedModifierIds.value.includes(m.id))
    .reduce((sum, m) => sum + Number(m.price_adjustment || 0), 0)

  return (Number(selectedVariant.value.price) + modifierTotal) * quantity.value
})

const confirmAddToCart = () => {
  if (!selectedVariant.value)
    return

  const chosenModifiers = modifierGroups.value
    .flatMap(g => g.modifiers || [])
    .filter(m => selectedModifierIds.value.includes(m.id))
    .map(m => ({ id: m.id, name: m.name, price: m.price_adjustment }))

  store.addToCart({
    product_id: activeProduct.value.id,
    product_name: activeProduct.value.name,
    variant_id: selectedVariant.value.id,
    variant_name: selectedVariant.value.name,
    unit_price: selectedVariant.value.price,
    quantity: quantity.value,
    modifiers: chosenModifiers,
    notes: notes.value,
  })

  isDialogOpen.value = false
}

const goToCheckout = () => {
  router.push({ name: 'order-session-token-checkout', params: { sessionToken: route.params.sessionToken } })
}

const goToStatus = () => {
  router.push({ name: 'order-session-token-status', params: { sessionToken: route.params.sessionToken } })
}
</script>

<template>
  <div class="menu-screen" :class="{ 'pb-24': store.cartCount > 0, 'pb-6': !store.cartCount }">
    <VAppBar flat color="surface" density="comfortable" class="menu-appbar">
      <VAppBarTitle>
        <div class="d-flex align-center gap-2">
          <VIcon icon="ri-table-line" color="primary" size="20" />
          <span class="font-weight-medium">{{ store.tableLabel }}</span>
        </div>
      </VAppBarTitle>
      <template #append>
        <VBtn icon variant="tonal" color="primary" size="small" @click="goToStatus">
          <VIcon icon="ri-receipt-line" size="18" />
        </VBtn>
      </template>
    </VAppBar>

    <div class="px-4">
      <OrderProgress :step="2" />
    </div>

    <!-- Category nav -->
    <div v-if="!isLoading && categories.length > 1" class="menu-categories px-4 mb-2">
      <button
        class="menu-chip"
        :class="{ 'is-active': activeCategory === 'all' }"
        @click="activeCategory = 'all'"
      >
        Semua
      </button>
      <button
        v-for="cat in categories"
        :key="cat"
        class="menu-chip"
        :class="{ 'is-active': activeCategory === cat }"
        @click="activeCategory = cat"
      >
        {{ cat }}
      </button>
    </div>

    <VContainer>
      <template v-if="isLoading">
        <VRow>
          <VCol v-for="n in 4" :key="n" cols="12" sm="6">
            <VSkeletonLoader type="image, list-item-two-line" rounded="lg" />
          </VCol>
        </VRow>
      </template>

      <VAlert v-else-if="loadError" type="error" variant="tonal" rounded="lg">
        {{ loadError }}
        <template #append>
          <VBtn size="small" variant="text" @click="loadMenu">
            Muat Ulang
          </VBtn>
        </template>
      </VAlert>

      <div v-else-if="!store.menu.length" class="menu-empty text-center py-12">
        <VIcon icon="ri-restaurant-2-line" size="56" color="disabled" class="mb-3" />
        <p class="text-body-1 font-weight-medium mb-1">
          Menu belum tersedia
        </p>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Silakan hubungi staf kami untuk bantuan.
        </p>
      </div>

      <template v-else>
        <div v-for="(products, categoryName) in visibleGroups" :key="categoryName" class="mb-7">
          <h6 class="text-h6 font-weight-bold mb-3">
            {{ categoryName }}
          </h6>

          <VRow>
            <VCol v-for="product in products" :key="product.id" cols="12" sm="6">
              <VCard class="menu-product-card" elevation="0" @click="openProduct(product)">
                <div class="menu-product-image">
                  <VImg
                    v-if="product.image_url"
                    :src="product.image_url"
                    aspect-ratio="1.3"
                    cover
                  />
                  <div v-else class="menu-product-placeholder">
                    <VIcon icon="ri-cup-line" size="32" color="primary" />
                  </div>
                </div>
                <div class="pa-3">
                  <p class="text-body-1 font-weight-medium mb-1 menu-product-name">
                    {{ product.name }}
                  </p>
                  <p class="text-caption text-medium-emphasis mb-3 menu-product-desc">
                    {{ product.description }}
                  </p>
                  <div class="d-flex align-center justify-space-between">
                    <span class="text-primary font-weight-bold">
                      {{ formatRupiah(product.variants?.[0]?.price) }}
                    </span>
                    <VBtn icon size="small" color="primary" class="menu-add-btn">
                      <VIcon icon="ri-add-line" size="18" />
                    </VBtn>
                  </div>
                </div>
              </VCard>
            </VCol>
          </VRow>
        </div>
      </template>
    </VContainer>

    <!-- Cart bar -->
    <VSlideYTransition>
      <div v-if="store.cartCount > 0" class="cart-bar pa-3">
        <VBtn block size="large" color="primary" rounded="lg" class="cart-bar-btn" @click="goToCheckout">
          <div class="d-flex align-center justify-space-between" style="inline-size: 100%;">
            <span class="d-flex align-center gap-2">
              <VChip color="white" variant="flat" size="small" class="text-primary font-weight-bold">
                {{ store.cartCount }}
              </VChip>
              {{ formatRupiah(store.cartSubtotal) }}
            </span>
            <span class="d-flex align-center">
              Lihat Keranjang
              <VIcon icon="ri-arrow-right-line" end />
            </span>
          </div>
        </VBtn>
      </div>
    </VSlideYTransition>

    <!-- Product detail dialog -->
    <VDialog v-model="isDialogOpen" max-width="480" scrollable>
      <VCard v-if="activeProduct" class="product-dialog-card">
        <div class="position-relative">
          <VImg v-if="activeProduct.image_url" :src="activeProduct.image_url" height="240" cover />
          <div v-else class="menu-product-placeholder" style="block-size: 240px;">
            <VIcon icon="ri-cup-line" size="48" color="primary" />
          </div>
          <VBtn
            icon
            size="small"
            variant="flat"
            class="product-dialog-close"
            @click="isDialogOpen = false"
          >
            <VIcon icon="ri-close-line" />
          </VBtn>
        </div>

        <VCardItem>
          <VCardTitle class="text-h6 font-weight-bold">
            {{ activeProduct.name }}
          </VCardTitle>
          <VCardSubtitle style="white-space: normal;">
            {{ activeProduct.description }}
          </VCardSubtitle>
        </VCardItem>

        <VCardText class="pt-0" style="max-block-size: 40vh; overflow-y: auto;">
          <p class="text-subtitle-2 font-weight-bold mb-2">
            Pilih Varian
          </p>
          <div class="variant-options mb-4">
            <button
              v-for="variant in activeProduct.variants"
              :key="variant.id"
              type="button"
              class="variant-option"
              :class="{ 'is-selected': selectedVariantId === variant.id }"
              @click="selectedVariantId = variant.id"
            >
              <span>{{ variant.name }}</span>
              <span class="text-medium-emphasis">{{ formatRupiah(variant.price) }}</span>
            </button>
          </div>

          <template v-if="modifierGroups.length">
            <div v-for="group in modifierGroups" :key="group.id" class="mb-4">
              <p class="text-subtitle-2 font-weight-bold mb-2">
                {{ group.name }}
              </p>
              <VCheckbox
                v-for="modifier in group.modifiers"
                :key="modifier.id"
                v-model="selectedModifierIds"
                :value="modifier.id"
                density="compact"
                hide-details
                color="primary"
              >
                <template #label>
                  <div class="d-flex justify-space-between" style="inline-size: 100%;">
                    <span>{{ modifier.name }}</span>
                    <span class="text-medium-emphasis">+{{ formatRupiah(modifier.price_adjustment) }}</span>
                  </div>
                </template>
              </VCheckbox>
            </div>
          </template>

          <p class="text-subtitle-2 font-weight-bold mb-2">
            Catatan
          </p>
          <VTextarea
            v-model="notes"
            placeholder="Contoh: less sugar, tanpa es"
            rows="2"
            auto-grow
            variant="outlined"
            rounded="lg"
          />

          <div class="d-flex align-center justify-center gap-4 mt-4">
            <VBtn icon size="small" variant="tonal" color="primary" :disabled="quantity <= 1" @click="quantity--">
              <VIcon icon="ri-subtract-line" />
            </VBtn>
            <span class="text-h6 font-weight-bold" style="min-inline-size: 24px; text-align: center;">{{ quantity }}</span>
            <VBtn icon size="small" variant="tonal" color="primary" @click="quantity++">
              <VIcon icon="ri-add-line" />
            </VBtn>
          </div>
        </VCardText>

        <VCardActions class="pa-4 pt-3 product-dialog-footer">
          <VBtn block size="large" rounded="lg" color="primary" :disabled="!selectedVariant" @click="confirmAddToCart">
            Tambah ke Keranjang · {{ formatRupiah(dialogTotal) }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.menu-screen {
  background: rgb(var(--v-theme-background));
  min-block-size: 100vh;
}

.menu-appbar {
  border-block-end: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.menu-categories {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-block: 4px 8px;
  scrollbar-width: none;
}

.menu-categories::-webkit-scrollbar { display: none; }

.menu-chip {
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  background: rgba(var(--v-theme-on-surface), 0.06);
  color: rgba(var(--v-theme-on-surface), 0.6);
  transition: background-color .2s ease, color .2s ease;
}

.menu-chip.is-active {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.menu-product-card {
  border-radius: 18px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  transition: transform .2s ease, box-shadow .2s ease;
  cursor: pointer;
}

.menu-product-card:hover {
  transform: translateY(-3px) scale(1.01);
  box-shadow: 0 16px 32px -20px rgba(var(--v-theme-on-surface), 0.35);
}

.menu-product-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  block-size: 140px;
  background: rgba(var(--v-theme-primary), 0.08);
}

.menu-product-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu-product-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-block-size: 2.2em;
}

.menu-add-btn {
  box-shadow: 0 6px 14px -4px rgba(var(--v-theme-primary), 0.6);
}

.cart-bar {
  position: fixed;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: 10;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 -8px 24px -8px rgba(var(--v-theme-on-surface), 0.2);
}

.cart-bar-btn {
  box-shadow: 0 10px 24px -8px rgba(var(--v-theme-primary), 0.6);
}

.product-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.product-dialog-close {
  position: absolute;
  inset-block-start: 12px;
  inset-inline-end: 12px;
  background: rgba(0, 0, 0, 0.45) !important;
  color: white;
}

.product-dialog-footer {
  border-block-start: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.variant-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.variant-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.1);
  font-size: 14px;
  font-weight: 500;
  transition: border-color .2s ease, background-color .2s ease;
}

.variant-option.is-selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.06);
  color: rgb(var(--v-theme-primary));
}
</style>
